import React, { useContext, useState } from 'react';
import { Box, ResponsiveContext } from 'grommet';
import { DateTime } from 'luxon';
import SHDButton from '@ui/Button/SHDButton';
import SHDFooter from '@ui/Footer/SHDFooter';
import SHDHeading from '@ui/Heading/SHDHeading';
import SHDInput from '@ui/Input/SHDInput';
import SHDModalContainer from '@ui/ModalContainer/SHDModalContainer';
import SHDSelect from '@ui/Select/SHDSelect';
import SHDSelectable from '@ui/Selectable/SHDSelectable';
import SHDText from '@ui/Text/SHDText';
import useFileUpload from '../../hooks/useFileUpload';
import useSaveTeamInfo from '../../hooks/useSaveTeamInfo';

import {
  ImageCategory,
  ImageUploadResponse,
  TeamInfoPayload,
} from '../../hooks/hookTypes.d';
import type { SelectOption, SportType } from '@theme/theme.d';
import LogoUploader from '@components/LogoUploader';
import { CollectTeamInfoProps } from './Onboarding';
import { SportsTypeName } from '@theme/theme.d';
import SHDLoader from '@ui/Loader/SHDLoader';

import { getTeamAgeLevelOptions } from '@helpers/utils/teamUtils';

const CollectTeamInfo: React.FC<CollectTeamInfoProps> = ({
  sport,
  onContinue,
}) => {
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [uploadedImage, setUploadedImage] = useState<ImageUploadResponse>();
  const [teamName, setTeamName] = useState<string>('');
  const [teamLevel, setTeamLevel] = useState<SelectOption>({
    label: '',
    value: '',
  });
  const [hasUnder13, setHasUnder13] = useState<boolean>(true);
  const screenSize = useContext(ResponsiveContext);

  const { uploading, uploadFile, error: uploadError } = useFileUpload();
  const { saving, saveTeamInfo } = useSaveTeamInfo();
  const [formError, setFormError] = useState<string | null>(null);

  const handleUploadLogo = async (file: File) => {
    try {
      const uploadImage = await uploadFile(file, ImageCategory.LOGO);
      if (uploadImage) {
        setLogoUrl(uploadImage.urlT150 || '');
        setUploadedImage(uploadImage);
      } else {
        setFormError(
          uploadError || 'An error occurred while uploading the image.'
        );
      }
    } catch (err) {
      setFormError('An unexpected error occurred during image upload.');
    }
  };

  const handleContinue = async () => {
    setFormError(null);

    if (!teamName) {
      setFormError('Team name is required.');
      return;
    }

    if (!teamLevel.value) {
      setFormError('Team level is required.');
      return;
    }

    const localTzString = DateTime.local().zoneName;

    const payload: TeamInfoPayload = {
      nameLong: teamName,
      sport: SportsTypeName[sport as SportType],
      attrib_ageLevel: teamLevel.value as string,
      isAge13Plus: hasUnder13,
      localTzString,
      imageId: uploadedImage?.id,
    };

    try {
      setFormError(null);

      const teamInfo = await saveTeamInfo(payload);
      if (teamInfo) {
        onContinue(true, teamInfo);
      } else {
        setFormError('An error occurred while saving team information.');
        onContinue(false);
      }
    } catch (err) {
      setFormError(
        'An unexpected error occurred while saving team information.'
      );
      onContinue(false);
    }
  };

  const selectOptions: Array<SelectOption> = getTeamAgeLevelOptions();

  const isContinueDisabled =
    uploading || saving || !teamName || !teamLevel.value || hasUnder13 === null;

  return (
    <>
      <SHDModalContainer>
        <SHDHeading level={3}>Tell us about your team</SHDHeading>
        <SHDText style="footnote">
          Put the finishing touches on your new team
        </SHDText>
        <Box align="center" margin={{ vertical: 'small' }}>
          {uploading ? (
            <Box margin={{ vertical: 'xlarge' }}>
              <SHDLoader size={62} />
            </Box>
          ) : (
            <LogoUploader
              logoUrl={logoUrl}
              teamName={teamName}
              onSave={handleUploadLogo}
            />
          )}
        </Box>
        <Box
          direction={screenSize !== 'medium' ? 'row' : 'column'}
          gap="xxsmall"
          fill="horizontal"
          margin={{ bottom: 'small' }}
        >
          <Box basis={screenSize !== 'medium' ? '60%' : '100%'}>
            <Box gap="xxsmall">
              <SHDInput
                label="Team name"
                onChange={(val) => setTeamName(val)}
                value={teamName}
                maxLength={30}
              />
              <SHDText style="body" color="#B2B2B2">
                *30 characters or fewer
              </SHDText>
            </Box>
          </Box>
          <Box basis={screenSize !== 'medium' ? '40%' : '100%'}>
            <SHDSelect
              options={selectOptions}
              onChange={(selection: SelectOption) => setTeamLevel(selection)}
              label="Team level"
              value={teamLevel}
            />
          </Box>
        </Box>
        <Box margin={{ top: 'xlarge' }}>
          <SHDText style="body">
            Are there any players under the age of 13 on your team?
          </SHDText>
          <Box direction="row" gap="small" margin={{ top: 'xxsmall' }}>
            <SHDSelectable
              id={'yes'}
              isActive={hasUnder13 === true}
              onSelected={() => setHasUnder13(true)}
            >
              <SHDText style="subhead-bold">Yes</SHDText>
            </SHDSelectable>
            <SHDSelectable
              id={'no'}
              isActive={hasUnder13 === false}
              onSelected={() => setHasUnder13(false)}
            >
              <SHDText style="subhead-bold">No</SHDText>
            </SHDSelectable>
          </Box>
        </Box>
        {formError && (
          <Box margin={{ vertical: 'small' }}>
            <SHDText style="body" color="status-error">
              {formError}
            </SHDText>
          </Box>
        )}
      </SHDModalContainer>
      <SHDFooter>
        <SHDModalContainer>
          <SHDButton
            primary
            label={saving ? 'Saving...' : 'Continue'}
            disabled={isContinueDisabled}
            onClick={handleContinue}
          />
        </SHDModalContainer>
      </SHDFooter>
    </>
  );
};

export default CollectTeamInfo;
